import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Spin, ConfigProvider, theme as antTheme, App as AntApp } from 'antd';
import MainLayout from './layouts/MainLayout';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useTheme } from './context/ThemeContext';
import { publicRoutes, protectedRoutes, printRoutes } from './routes';
import barcodeRoutes from './modules/barcode/routes';
import { NotificationProvider } from './context/NotificationContext';
import { setupAxiosInterceptors } from './services/config/axios.interceptor';
import { playErrorTone, playSuccessTone } from './utils/sound';

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, selectedBranch, token } = useAuth();

  // Check if token exists and is valid
  if (!isAuthenticated || !token) {
    return <Navigate to="/login" replace />;
  }

  if (!selectedBranch) {
    return <Navigate to="/select-branch" replace />;
  }

  return <>{children}</>;
};

const LoadingScreen = () => (
  <div style={{ textAlign: 'center', padding: '50px' }}>
    <Spin size="large" />
  </div>
);

// Patches antd message globally so every message.error / message.success plays a tone
const SoundBridge: React.FC = () => {
  const { message } = AntApp.useApp();

  useEffect(() => {
    const originalError = message.error.bind(message);
    const originalSuccess = message.success.bind(message);

    (message as any).error = (...args: any[]) => {
      playErrorTone();
      return (originalError as any)(...args);
    };

    (message as any).success = (...args: any[]) => {
      playSuccessTone();
      return (originalSuccess as any)(...args);
    };
  }, [message]);

  return null;
};

// Component to setup interceptors
const InterceptorSetup: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    setupAxiosInterceptors(logout, navigate);
  }, [logout, navigate]);

  return null;
};

const AppContent: React.FC = () => {
  const { theme, isDarkMode } = useTheme();
  const { defaultAlgorithm, darkAlgorithm } = antTheme;

  return (
    <ConfigProvider
      theme={{
        ...theme,
        algorithm: isDarkMode ? darkAlgorithm : defaultAlgorithm,
      }}
    >
      <AuthProvider>
        <NotificationProvider>
          <AntApp>
            <SoundBridge />
            <BrowserRouter>
            <InterceptorSetup />
            <Suspense fallback={<LoadingScreen />}>
              <Routes>
                {/* Public Routes */}
                {publicRoutes.map((route) => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={route.element}
                  />
                ))}

                {/* Public Barcode Routes (no auth) */}
                <Route path="/barcode">
                  {barcodeRoutes.map((route) => (
                    <Route
                      key={route.path || 'index'}
                      index={route.index}
                      path={route.path}
                      element={route.element}
                    />
                  ))}
                </Route>

                {/* Authenticated, chrome-free print/report routes — no
                    MainLayout (no Sidebar/Header), each page fetches its own
                    data so it's directly linkable and refreshable. */}
                {printRoutes.map((route) => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={<ProtectedRoute>{route.element}</ProtectedRoute>}
                  />
                ))}

                {/* Protected Routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <MainLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="/control-panel" replace />} />
                  {protectedRoutes.map((moduleRoute) => (
                    <Route
                      key={moduleRoute.path}
                      path={moduleRoute.path}
                      element={moduleRoute.element}
                    >
                      {moduleRoute.children?.map((route) => (
                        <Route
                          key={route.path || 'index'}
                          index={route.index}
                          path={route.path}
                          element={route.element}
                        />
                      ))}
                    </Route>
                  ))}
                </Route>
              </Routes>
            </Suspense>
            </BrowserRouter>
          </AntApp>
        </NotificationProvider>
      </AuthProvider>
    </ConfigProvider>
  );
};

const App: React.FC = () => {
  return <AppContent />;
};

export default App;
